import type { ReactNode } from "react";
/**
 * İçerik bölümleri için vektörel (inline SVG) çizimler: yol haritası, adım şeridi,
 * kart ızgarası, hub şeması, huni ve soru kartları. Uzun metinleri satırlara
 * bölerek gösterir; masaüstü ve mobil için ayrı yerleşim. Dosya yüklenmez,
 * CLS/LCP etkisi yoktur. Marka dili: siyah kalın çizgi, beyaz kutu, lime vurgu.
 */

const LIME = "#9EFF00";
const INK = "#000";
const MUTED = "#525252";

export function satirla(metin: string, maxKarakter: number, maxSatir = 2): string[] {
  const kelimeler = metin.split(" ");
  const satirlar: string[] = [];
  let cur = "";
  for (const k of kelimeler) {
    if ((cur + " " + k).trim().length > maxKarakter && cur) {
      satirlar.push(cur);
      cur = k;
    } else cur = (cur + " " + k).trim();
  }
  if (cur) satirlar.push(cur);
  if (satirlar.length > maxSatir) {
    const kes = satirlar.slice(0, maxSatir);
    kes[maxSatir - 1] = kes[maxSatir - 1].replace(/[,;:]?\s*$/, "") + "…";
    return kes;
  }
  return satirlar;
}

function Metin({ x, y, satirlar, boyut, kalin, renk = INK, hiza = "middle", mono = false }: {
  x: number; y: number; satirlar: string[]; boyut: number; kalin?: boolean; renk?: string; hiza?: "middle" | "start"; mono?: boolean;
}) {
  const satirY = boyut * 1.25;
  const baslangic = y - ((satirlar.length - 1) * satirY) / 2;
  return (
    <text x={x} y={baslangic} textAnchor={hiza} fontSize={boyut} fontWeight={kalin ? 900 : 700} fill={renk}
      fontFamily={mono ? "ui-monospace, SFMono-Regular, Menlo, monospace" : "inherit"} dominantBaseline="middle">
      {satirlar.map((s, i) => (
        <tspan key={i} x={x} dy={i === 0 ? 0 : satirY}>{s}</tspan>
      ))}
    </text>
  );
}

function Kutu({ x, y, w, h, vurgu = false, golge = 6 }: { x: number; y: number; w: number; h: number; vurgu?: boolean; golge?: number }) {
  return (
    <g>
      <rect x={x + golge} y={y + golge} width={w} height={h} fill={vurgu ? INK : LIME} />
      <rect x={x} y={y} width={w} height={h} fill={vurgu ? LIME : "#fff"} stroke={INK} strokeWidth={4} />
    </g>
  );
}

function Numara({ x, y, n, boyut = 30 }: { x: number; y: number; n: number; boyut?: number }) {
  return (
    <g>
      <rect x={x} y={y} width={boyut + 8} height={boyut - 4} fill={INK} />
      <text x={x + (boyut + 8) / 2} y={y + (boyut - 4) / 2 + 1} textAnchor="middle" fontSize={boyut * 0.47} fontWeight={900} fill={LIME}
        fontFamily="ui-monospace, Menlo, monospace" dominantBaseline="middle">
        {String(n).padStart(2, "0")}
      </text>
    </g>
  );
}

function Tik({ x, y, s = 22 }: { x: number; y: number; s?: number }) {
  return (
    <g>
      <rect x={x + 3} y={y + 3} width={s} height={s} fill={INK} />
      <rect x={x} y={y} width={s} height={s} fill={LIME} stroke={INK} strokeWidth={3} />
      <polyline points={`${x + s * 0.22},${y + s * 0.52} ${x + s * 0.43},${y + s * 0.72} ${x + s * 0.8},${y + s * 0.3}`} fill="none" stroke={INK} strokeWidth={3.5} strokeLinecap="round" strokeLinejoin="round" />
    </g>
  );
}

const OK_ID = "dj-ok-icerik";
function OkIsareti() {
  return (
    <defs>
      <marker id={OK_ID} viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
        <path d="M0,0 L10,5 L0,10 z" fill={INK} />
      </marker>
    </defs>
  );
}

/* ------------------------------------------------------------------ */
/* 1) YOL HARİTASI: bölüm başlıkları sıralı şerit                       */
/* ------------------------------------------------------------------ */
export function YolHaritasi({ basliklar }: { basliklar: string[] }) {
  const n = Math.max(1, basliklar.length);
  const W = 1000, pad = 10, bosluk = n >= 5 ? 22 : 30;
  const kw = (W - pad * 2 - bosluk * (n - 1)) / n;
  const kh = 128, ky = 40, H = ky + kh + 24;
  const kar = Math.max(10, Math.floor((kw - 28) / 8.4));
  const fb = n >= 5 ? 12.5 : 13.5;
  const Yatay = (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" role="img" aria-label="Rehber yol haritası">
      <OkIsareti />
      {basliklar.map((b, i) => {
        const x = pad + i * (kw + bosluk);
        const son = i === n - 1;
        return (
          <g key={i}>
            <Kutu x={x} y={ky} w={kw} h={kh} vurgu={son} />
            <Numara x={x + 10} y={ky - 14} n={i + 1} />
            <Metin x={x + kw / 2} y={ky + kh / 2 + 8} satirlar={satirla(b, kar, 4)} boyut={fb} kalin />
            {!son && <line x1={x + kw + 4} y1={ky + kh / 2} x2={x + kw + bosluk - 4} y2={ky + kh / 2} stroke={INK} strokeWidth={4} markerEnd={`url(#${OK_ID})`} />}
          </g>
        );
      })}
    </svg>
  );
  const dW = 360, dH = 10 + n * 84;
  const Dikey = (
    <svg viewBox={`0 0 ${dW} ${dH}`} className="w-full h-auto" role="img" aria-label="Rehber yol haritası">
      <OkIsareti />
      {basliklar.map((b, i) => {
        const y = 6 + i * 84;
        const son = i === n - 1;
        return (
          <g key={i}>
            <Kutu x={8} y={y} w={336} h={62} vurgu={son} golge={5} />
            <Numara x={18} y={y + 17} n={i + 1} boyut={28} />
            <Metin x={66} y={y + 31} satirlar={satirla(b, 36, 2)} boyut={13} kalin hiza="start" />
            {!son && <line x1={dW / 2} y1={y + 68} x2={dW / 2} y2={y + 82} stroke={INK} strokeWidth={4} markerEnd={`url(#${OK_ID})`} />}
          </g>
        );
      })}
    </svg>
  );
  return (
    <>
      <div className="hidden md:block">{Yatay}</div>
      <div className="md:hidden">{Dikey}</div>
    </>
  );
}

/* ------------------------------------------------------------------ */
/* 2) ADIM ŞERİDİ: numaralı zikzak zaman çizelgesi                       */
/* ------------------------------------------------------------------ */
export function AdimSeridi({ maddeler }: { maddeler: string[] }) {
  const n = maddeler.length;
  const W = 1000, adimH = 132, kw = 440, kh = 106, cx = W / 2;
  const H = 20 + n * adimH;
  const Yatay = (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" role="img" aria-label="Adım adım süreç çizimi">
      <line x1={cx} y1={10} x2={cx} y2={H - 10} stroke={INK} strokeWidth={5} strokeDasharray="2 12" strokeLinecap="round" />
      {maddeler.map((m, i) => {
        const y = 14 + i * adimH;
        const sol = i % 2 === 0;
        const x = sol ? cx - 60 - kw : cx + 60;
        const son = i === n - 1;
        return (
          <g key={i}>
            <line x1={sol ? x + kw : x} y1={y + kh / 2} x2={cx} y2={y + kh / 2} stroke={INK} strokeWidth={4} />
            <circle cx={cx} cy={y + kh / 2} r={24} fill={son ? LIME : "#fff"} stroke={INK} strokeWidth={5} />
            <text x={cx} y={y + kh / 2 + 1} textAnchor="middle" fontSize={15} fontWeight={900} fill={INK} fontFamily="ui-monospace, Menlo, monospace" dominantBaseline="middle">
              {String(i + 1).padStart(2, "0")}
            </text>
            <Kutu x={x} y={y} w={kw} h={kh} vurgu={son} golge={5} />
            <Metin x={x + kw / 2} y={y + kh / 2} satirlar={satirla(m, 54, 4)} boyut={13} kalin />
          </g>
        );
      })}
    </svg>
  );
  const dW = 360, dAdim = 116, dkh = 98;
  const dH = 12 + n * dAdim;
  const Dikey = (
    <svg viewBox={`0 0 ${dW} ${dH}`} className="w-full h-auto" role="img" aria-label="Adım adım süreç çizimi">
      <line x1={30} y1={10} x2={30} y2={dH - 14} stroke={INK} strokeWidth={4} strokeDasharray="2 10" strokeLinecap="round" />
      {maddeler.map((m, i) => {
        const y = 8 + i * dAdim;
        const son = i === n - 1;
        return (
          <g key={i}>
            <circle cx={30} cy={y + dkh / 2} r={18} fill={son ? LIME : "#fff"} stroke={INK} strokeWidth={4} />
            <text x={30} y={y + dkh / 2 + 1} textAnchor="middle" fontSize={12} fontWeight={900} fill={INK} fontFamily="ui-monospace, Menlo, monospace" dominantBaseline="middle">
              {String(i + 1).padStart(2, "0")}
            </text>
            <Kutu x={62} y={y} w={288} h={dkh} vurgu={son} golge={4} />
            <Metin x={62 + 144} y={y + dkh / 2} satirlar={satirla(m, 36, 5)} boyut={11} kalin />
          </g>
        );
      })}
    </svg>
  );
  return (
    <>
      <div className="hidden md:block">{Yatay}</div>
      <div className="md:hidden">{Dikey}</div>
    </>
  );
}

/* ------------------------------------------------------------------ */
/* 3) KART IZGARASI: onay işaretli kontrol listesi kartları              */
/* ------------------------------------------------------------------ */
export function KartIzgara({ maddeler }: { maddeler: string[] }) {
  const n = maddeler.length;
  const sutun = n <= 4 ? 2 : 3;
  const W = 1000, pad = 10, bosluk = 24;
  const kw = (W - pad * 2 - bosluk * (sutun - 1)) / sutun;
  const kh = 124, satir = Math.ceil(n / sutun);
  const H = pad * 2 + satir * kh + (satir - 1) * bosluk + 8;
  const kar = Math.floor((kw - 70) / 7.4);
  const Yatay = (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" role="img" aria-label="Kontrol listesi çizimi">
      {maddeler.map((m, i) => {
        const x = pad + (i % sutun) * (kw + bosluk);
        const y = pad + Math.floor(i / sutun) * (kh + bosluk);
        return (
          <g key={i}>
            <Kutu x={x} y={y} w={kw} h={kh} golge={5} />
            <Tik x={x + 16} y={y + 16} />
            <Metin x={x + 54} y={y + kh / 2 + 6} satirlar={satirla(m, kar, 4)} boyut={13} kalin hiza="start" />
          </g>
        );
      })}
    </svg>
  );
  const dW = 360, dkh = 92, dH = 8 + n * (dkh + 14);
  const Dikey = (
    <svg viewBox={`0 0 ${dW} ${dH}`} className="w-full h-auto" role="img" aria-label="Kontrol listesi çizimi">
      {maddeler.map((m, i) => {
        const y = 4 + i * (dkh + 14);
        return (
          <g key={i}>
            <Kutu x={8} y={y} w={340} h={dkh} golge={4} />
            <Tik x={20} y={y + 14} s={20} />
            <Metin x={52} y={y + dkh / 2} satirlar={satirla(m, 36, 4)} boyut={11.5} kalin hiza="start" />
          </g>
        );
      })}
    </svg>
  );
  return (
    <>
      <div className="hidden md:block">{Yatay}</div>
      <div className="md:hidden">{Dikey}</div>
    </>
  );
}

/* ------------------------------------------------------------------ */
/* 4) HUB ŞEMASI: merkez + çevre kutular (mobilde ağaç)                  */
/* ------------------------------------------------------------------ */
export function HubSema({ merkez, dallar }: { merkez: string; dallar: string[] }) {
  const n = dallar.length;
  const W = 1000, H = 640, cx = W / 2, cy = H / 2;
  const dw = 250, dh = 100, rx = 350, ry = 230;
  const Yatay = (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" role="img" aria-label="İlişki şeması">
      {dallar.map((_, i) => {
        const aci = -Math.PI / 2 + (i * 2 * Math.PI) / n;
        return <line key={i} x1={cx} y1={cy} x2={cx + rx * Math.cos(aci)} y2={cy + ry * Math.sin(aci)} stroke={INK} strokeWidth={4} strokeDasharray="2 10" strokeLinecap="round" />;
      })}
      <circle cx={cx + 8} cy={cy + 8} r={118} fill={INK} />
      <circle cx={cx} cy={cy} r={118} fill={LIME} stroke={INK} strokeWidth={5} />
      <Metin x={cx} y={cy} satirlar={satirla(merkez, 20, 3)} boyut={17} kalin />
      {dallar.map((d, i) => {
        const aci = -Math.PI / 2 + (i * 2 * Math.PI) / n;
        const x = cx + rx * Math.cos(aci) - dw / 2, y = cy + ry * Math.sin(aci) - dh / 2;
        return (
          <g key={i}>
            <Kutu x={x} y={y} w={dw} h={dh} golge={5} />
            <Numara x={x + 8} y={y - 12} n={i + 1} boyut={26} />
            <Metin x={x + dw / 2} y={y + dh / 2 + 4} satirlar={satirla(d, 30, 3)} boyut={12.5} kalin />
          </g>
        );
      })}
    </svg>
  );
  const dW = 360, ustH = 96, dkh = 84, dH = 10 + ustH + 26 + n * (dkh + 14);
  const Dikey = (
    <svg viewBox={`0 0 ${dW} ${dH}`} className="w-full h-auto" role="img" aria-label="İlişki şeması">
      <Kutu x={8} y={6} w={340} h={ustH} vurgu golge={5} />
      <Metin x={178} y={6 + ustH / 2} satirlar={satirla(merkez, 34, 3)} boyut={14} kalin />
      <line x1={30} y1={6 + ustH + 6} x2={30} y2={dH - 16} stroke={INK} strokeWidth={4} strokeDasharray="2 10" strokeLinecap="round" />
      {dallar.map((d, i) => {
        const y = 6 + ustH + 26 + i * (dkh + 14);
        return (
          <g key={i}>
            <line x1={30} y1={y + dkh / 2} x2={60} y2={y + dkh / 2} stroke={INK} strokeWidth={4} />
            <Kutu x={62} y={y} w={288} h={dkh} golge={4} />
            <Numara x={70} y={y - 10} n={i + 1} boyut={24} />
            <Metin x={62 + 144} y={y + dkh / 2 + 4} satirlar={satirla(d, 34, 3)} boyut={11.5} kalin />
          </g>
        );
      })}
    </svg>
  );
  return (
    <>
      <div className="hidden md:block">{Yatay}</div>
      <div className="md:hidden">{Dikey}</div>
    </>
  );
}

/* ------------------------------------------------------------------ */
/* 5) HUNİ: daralan katmanlar                                            */
/* ------------------------------------------------------------------ */
export function HuniSema({ katmanlar }: { katmanlar: string[] }) {
  const n = katmanlar.length;
  const W = 900, katH = 104, ust = 16, H = ust + n * katH + 10;
  const genislikler = katmanlar.map((_, i) => 840 - i * (360 / Math.max(1, n - 1)));
  const Yatay = (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" role="img" aria-label="Huni çizimi">
      {katmanlar.map((k, i) => {
        const wUst = genislikler[i];
        const wAlt = i < n - 1 ? genislikler[i + 1] : wUst - 90;
        const y = ust + i * katH;
        const x1 = (W - wUst) / 2, x2 = (W + wUst) / 2, x3 = (W + wAlt) / 2, x4 = (W - wAlt) / 2;
        const son = i === n - 1;
        const kar = Math.floor(Math.min(wUst, wAlt) / 8.2);
        return (
          <g key={i}>
            <polygon points={`${x1 + 6},${y + 6} ${x2 + 6},${y + 6} ${x3 + 6},${y + katH - 8 + 6} ${x4 + 6},${y + katH - 8 + 6}`} fill={son ? INK : LIME} />
            <polygon points={`${x1},${y} ${x2},${y} ${x3},${y + katH - 8} ${x4},${y + katH - 8}`} fill={son ? LIME : "#fff"} stroke={INK} strokeWidth={4} />
            <Numara x={x1 + 14} y={y + 12} n={i + 1} boyut={26} />
            <Metin x={W / 2} y={y + (katH - 8) / 2} satirlar={satirla(k, kar, 3)} boyut={13.5} kalin />
          </g>
        );
      })}
    </svg>
  );
  const dW = 360, dKat = 92, dH = 10 + n * dKat;
  const dGen = katmanlar.map((_, i) => 344 - i * (110 / Math.max(1, n - 1)));
  const Dikey = (
    <svg viewBox={`0 0 ${dW} ${dH}`} className="w-full h-auto" role="img" aria-label="Huni çizimi">
      {katmanlar.map((k, i) => {
        const wUst = dGen[i];
        const wAlt = i < n - 1 ? dGen[i + 1] : wUst - 40;
        const y = 6 + i * dKat;
        const x1 = (dW - wUst) / 2, x2 = (dW + wUst) / 2, x3 = (dW + wAlt) / 2, x4 = (dW - wAlt) / 2;
        const son = i === n - 1;
        return (
          <g key={i}>
            <polygon points={`${x1 + 4},${y + 4} ${x2 + 4},${y + 4} ${x3 + 4},${y + dKat - 8 + 4} ${x4 + 4},${y + dKat - 8 + 4}`} fill={son ? INK : LIME} />
            <polygon points={`${x1},${y} ${x2},${y} ${x3},${y + dKat - 8} ${x4},${y + dKat - 8}`} fill={son ? LIME : "#fff"} stroke={INK} strokeWidth={3} />
            <Metin x={dW / 2} y={y + (dKat - 8) / 2} satirlar={satirla(k, Math.floor(Math.min(wUst, wAlt) / 8), 3)} boyut={11.5} kalin />
          </g>
        );
      })}
    </svg>
  );
  return (
    <>
      <div className="hidden md:block">{Yatay}</div>
      <div className="md:hidden">{Dikey}</div>
    </>
  );
}

/* ------------------------------------------------------------------ */
/* 6) SORU KARTLARI                                                      */
/* ------------------------------------------------------------------ */
export function SoruKartlari({ sorular }: { sorular: string[] }) {
  const n = sorular.length;
  const W = 1000, pad = 10, bosluk = 24, sutun = 2;
  const kw = (W - pad * 2 - bosluk) / sutun, kh = 108, satir = Math.ceil(n / sutun);
  const H = pad * 2 + satir * kh + (satir - 1) * bosluk + 8;
  const Yatay = (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" role="img" aria-label="Sık sorulan sorular çizimi">
      {sorular.map((s, i) => {
        const x = pad + (i % sutun) * (kw + bosluk);
        const y = pad + Math.floor(i / sutun) * (kh + bosluk);
        return (
          <g key={i}>
            <Kutu x={x} y={y} w={kw} h={kh} golge={5} />
            <circle cx={x + 36} cy={y + kh / 2} r={20} fill={LIME} stroke={INK} strokeWidth={4} />
            <text x={x + 36} y={y + kh / 2 + 1} textAnchor="middle" fontSize={22} fontWeight={900} fill={INK} dominantBaseline="middle">?</text>
            <Metin x={x + 70} y={y + kh / 2} satirlar={satirla(s, Math.floor((kw - 90) / 7.6), 3)} boyut={13.5} kalin hiza="start" />
          </g>
        );
      })}
    </svg>
  );
  const dW = 360, dkh = 86, dH = 8 + n * (dkh + 14);
  const Dikey = (
    <svg viewBox={`0 0 ${dW} ${dH}`} className="w-full h-auto" role="img" aria-label="Sık sorulan sorular çizimi">
      {sorular.map((s, i) => {
        const y = 4 + i * (dkh + 14);
        return (
          <g key={i}>
            <Kutu x={8} y={y} w={340} h={dkh} golge={4} />
            <circle cx={34} cy={y + dkh / 2} r={16} fill={LIME} stroke={INK} strokeWidth={3} />
            <text x={34} y={y + dkh / 2 + 1} textAnchor="middle" fontSize={18} fontWeight={900} fill={INK} dominantBaseline="middle">?</text>
            <Metin x={60} y={y + dkh / 2} satirlar={satirla(s, 34, 3)} boyut={11.5} kalin hiza="start" />
          </g>
        );
      })}
    </svg>
  );
  return (
    <>
      <div className="hidden md:block">{Yatay}</div>
      <div className="md:hidden">{Dikey}</div>
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Figür sarmalayıcı + otomatik tür seçimi                               */
/* ------------------------------------------------------------------ */
export function Figur({ no, etiket, children }: { no: number; etiket: string; children: ReactNode }) {
  return (
    <figure className="my-6 border-3 border-black bg-neutral-50 p-4 md:p-6 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
      <figcaption className="mb-4 flex items-center gap-2">
        <span className="bg-black text-[#9EFF00] font-mono font-bold text-[10px] px-2 py-0.5 uppercase">Şema {String(no).padStart(2, "0")}</span>
        <span className="font-mono font-bold text-[11px] uppercase tracking-wider text-neutral-600">{etiket}</span>
      </figcaption>
      {children}
    </figure>
  );
}

export type GorselTuru = "adim" | "kart" | "hub" | "huni";

/** Sıra numarasına ve madde sayısına göre uygun çizim türünü seçer. */
export function gorselTuruSec(sira: number, n: number): GorselTuru {
  const dizi: GorselTuru[] = ["adim", "kart", "hub", "huni"];
  let t = dizi[sira % dizi.length];
  if (t === "huni" && (n < 3 || n > 5)) t = "adim";
  if (t === "hub" && (n < 3 || n > 6)) t = "kart";
  return t;
}

export const GORSEL_ETIKET: Record<GorselTuru, string> = {
  adim: "Adım adım",
  kart: "Kontrol listesi",
  hub: "İlişki şeması",
  huni: "Huni görünümü",
};

export function MaddeGorseli({ tur, maddeler, merkez }: { tur: GorselTuru; maddeler: string[]; merkez: string }) {
  if (tur === "adim") return <AdimSeridi maddeler={maddeler} />;
  if (tur === "kart") return <KartIzgara maddeler={maddeler} />;
  if (tur === "hub") return <HubSema merkez={merkez} dallar={maddeler} />;
  return <HuniSema katmanlar={maddeler} />;
}
